import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'verify' | 'forgot' | 'reset'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState('');
  const [err, setErr] = useState('');

  const msgFromError = (e, fallback = 'Something went wrong') => {
    try {
      const d = e?.response?.data;
      const det = d?.detail ?? d?.error ?? d;
      if (!det) return e?.message || fallback;
      if (typeof det === 'string') return det;
      if (Array.isArray(det)) {
        // FastAPI/Pydantic validation errors
        const msgs = det.map((x) => (x?.msg || JSON.stringify(x))).filter(Boolean);
        return msgs.join('; ');
      }
      if (typeof det === 'object') {
        if (det.msg) return det.msg;
        if (det.message) return det.message;
        try { return JSON.stringify(det); } catch { /* ignore */ }
      }
      return e?.message || fallback;
    } catch {
      return e?.message || fallback;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(mode === 'login' ? 'Signing you in…' : mode === 'register' ? 'Creating your account…' : 'Verifying…');
    setErr('');
    try {
      if (mode === 'register') {
        if (!email || !password) throw new Error('Email and password are required');
        try {
          const { data } = await axios.post('/auth/register', { email, password });
          if (data?.otp) {
            setOtp(String(data.otp || ''));
            setStatus(`Dev mode: verification code ${data.otp}`);
          } else {
            setStatus(data?.message || 'Verification code sent to your email.');
          }
          setMode('verify');
        } catch (e) {
          const code = e?.response?.status;
          const msg = msgFromError(e);
          if (code === 409 && /already/i.test(msg)) {
            // Email exists; try resending OTP
            setStatus('Email exists. Resending code…');
            await axios.post('/auth/resend-otp', { email });
            setStatus('Verification code sent to your email.');
            setMode('verify');
          } else {
            throw e;
          }
        }
        return;
      }

      if (mode === 'verify') {
        if (!email || !otp) throw new Error('Email and OTP are required');
        await axios.post('/auth/verify', { email, otp });
        setStatus('Verified! You can sign in now.');
        setMode('login');
        setOtp('');
        return;
      }

      if (mode === 'forgot') {
        if (!email) throw new Error('Email is required');
        await axios.post('/auth/forgot-password', { email });
        setStatus('If your email exists, a reset code was sent.');
        setMode('reset');
        return;
      }

      if (mode === 'reset') {
        if (!email || !otp || !newPassword) throw new Error('Email, reset code and new password are required');
        await axios.post('/auth/reset-password', { email, otp, new_password: newPassword });
        setStatus('Password updated. You can log in now.');
        setMode('login');
        setOtp('');
        setNewPassword('');
        return;
      }

      const { data } = await axios.post('/auth/login', { email, password });
      localStorage.setItem('maskan_auth_v2', JSON.stringify({ user: data.user, ts: Date.now() }));
      setStatus('Success! Redirecting…');
      setTimeout(() => navigate('/', { replace: true }), 300);
    } catch (e) {
      setErr(msgFromError(e));
      setStatus('');
    }
  };

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <h1 style={{ margin: '8px 0 4px', fontSize: 22 }}>Welcome to Maskan Hub</h1>
          <p style={{ margin: 0, color: '#9db0d3' }}>Sign in to continue</p>
        </div>

        <div style={styles.tabs}>
          <button type="button" onClick={() => { setMode('login'); setErr(''); setStatus(''); }} style={{ ...styles.tab, ...(mode === 'login' ? styles.tabActive : {}) }}>Login</button>
          <button type="button" onClick={() => { setMode('register'); setErr(''); setStatus(''); }} style={{ ...styles.tab, ...(mode === 'register' ? styles.tabActive : {}) }}>Register</button>
          {mode === 'verify' && (
            <button type="button" onClick={() => { setMode('verify'); setErr(''); setStatus(''); }} style={{ ...styles.tab, ...(mode === 'verify' ? styles.tabActive : {}) }}>Verify</button>
          )}
          {mode === 'forgot' && (
            <button type="button" onClick={() => { setMode('forgot'); setErr(''); setStatus(''); }} style={{ ...styles.tab, ...(mode === 'forgot' ? styles.tabActive : {}) }}>Forgot</button>
          )}
          {mode === 'reset' && (
            <button type="button" onClick={() => { setMode('reset'); setErr(''); setStatus(''); }} style={{ ...styles.tab, ...(mode === 'reset' ? styles.tabActive : {}) }}>Reset</button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <div style={styles.inputWrap}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={styles.input}
                required
              />
            </div>
          </div>

          {(mode === 'login' || mode === 'register') && (
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <div style={styles.inputWrap}>
              <input
                type={show ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                style={styles.input}
                required
              />
              <button type="button" onClick={() => setShow(!show)} style={styles.reveal}>
                {show ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          )}

          {mode === 'verify' && (
            <div style={styles.field}>
              <label style={styles.label}>Enter 6‑digit code</label>
              <div style={styles.inputWrap}>
                <input
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  style={styles.input}
                  required
                />
              </div>
              <div style={{ marginTop: 8, textAlign: 'right' }}>
                <button type="button" onClick={async () => {
                  try {
                    setStatus('Resending code…'); setErr('');
                    await axios.post('/auth/resend-otp', { email });
                    setStatus('Verification code sent');
                  } catch (e) { setErr(msgFromError(e, 'Failed to resend')); setStatus(''); }
                }} style={styles.linkBtn}>Resend code</button>
              </div>
            </div>
          )}

          {mode === 'forgot' && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#9db0d3' }}>
              Enter your email to receive a password reset code.
            </div>
          )}

          {mode === 'reset' && (
            <>
              <div style={styles.field}>
                <label style={styles.label}>Reset code</label>
                <div style={styles.inputWrap}>
                  <input
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    style={styles.input}
                    required
                  />
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>New password</label>
                <div style={styles.inputWrap}>
                  <input
                    type='password'
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    style={styles.input}
                    required
                  />
                </div>
              </div>
            </>
          )}

          <button type="submit" style={styles.submit}>Continue</button>
          {mode === 'login' && (
            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <button type="button" onClick={() => { setMode('forgot'); setStatus(''); setErr(''); setOtp(''); }} style={styles.linkBtn}>Forgot password?</button>
            </div>
          )}
          {status && <div style={{ marginTop: 8, color: '#60ffb0', fontSize: 12 }}>{status}</div>}
          {err && <div style={{ marginTop: 8, color: '#ff8b8b', fontSize: 12 }}>{err}</div>}
        </form>

        <div style={{ marginTop: 14, color: '#9db0d3', fontSize: 12, textAlign: 'center' }}>
          © {new Date().getFullYear()} Maskan Technologies
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: 28,
    background:
      'radial-gradient(1200px 800px at 10% -10%, rgba(124,58,237,.25), transparent 50%),\n' +
      'radial-gradient(800px 600px at 90% 110%, rgba(6,182,212,.25), transparent 50%),\n' +
      '#0f172a',
    color: '#e6eefc',
  },
  card: {
    width: 'min(460px, 92vw)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    backdropFilter: 'blur(12px)',
    boxShadow: '0 8px 40px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.02)',
    padding: 22,
  },
  tabs: {
    display: 'flex',
    gap: 8,
    background: '#0a101c',
    padding: 6,
    borderRadius: 12,
    margin: '12px 0 8px',
  },
  tab: {
    flex: 1,
    background: 'transparent',
    color: '#9db0d3',
    border: 'none',
    padding: '10px 12px',
    borderRadius: 10,
    cursor: 'pointer',
    fontWeight: 700,
    letterSpacing: '.3px',
  },
  tabActive: {
    background: '#111a2f',
    color: '#e6eefc',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)'
  },
  field: { margin: '12px 0' },
  label: { display: 'block', fontSize: 13, color: '#9db0d3', margin: '0 0 6px 2px' },
  inputWrap: { display: 'flex', alignItems: 'center', gap: 8, background: '#0a111f', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '10px 12px' },
  input: { flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#e6eefc', fontSize: 14 },
  reveal: { background: 'transparent', border: 'none', color: '#9db0d3', cursor: 'pointer', fontSize: 12 },
  submit: {
    width: '100%', marginTop: 10, padding: '12px 14px', border: 'none', borderRadius: 12, color: 'white', fontWeight: 800, cursor: 'pointer',
    background: 'linear-gradient(90deg, #7c3aed, #06b6d4)', boxShadow: '0 4px 20px rgba(124,58,237,.35)'
  },
  linkBtn: { background: 'transparent', border: 'none', color: '#9db0d3', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }
};
